import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  DocumentsKeyEnum,
  LegalDocsAcceptActions, LegalDocsCopyKeys,
  LegalDocsResponsePayloadItems,
  LegalDocumentsService
} from '../legal-documents.service';
import { Observable, Subscription, tap } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MessageBusEventUtil, MessageBusOutgoingEventKey } from '@micro-core/message-bus';
import { computed, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

export interface LegalDocsPageConfigInterface {
  showCloseButton?: boolean;
  showBackbutton?: boolean;
  closeButtonRoute?: string[];
  pageOnForwardCtaPath: string[];
  documentsKey: DocumentsKeyEnum;
}

interface KnownPageConfigInterface {
  bypassOnServerError?: boolean;
  documentsKey?: DocumentsKeyEnum;
  pageOnForwardCtaPath?: string[];
  closeButtonRoute?: string[];
  showCloseButton?: boolean;
  showBackbutton?: boolean;
  title?: string;
  subtitle?: string;
}

@Component({
    selector: 'brightside-web-required-legal-documents',
    templateUrl: './required-legal-documents.component.html',
    styleUrls: ['./required-legal-documents.component.scss'],
    providers: [LegalDocumentsService],
    standalone: false
})
export class RequiredLegalDocumentsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() documentsKey: string;
  @Input() pageOnForwardCtaPath: string[];
  @Input() showBackbutton = true;
  @Input() skipActionEmit = true;
  @Input() showCloseButton = false;
  @Input() closeButtonRoute: string[] | undefined;
  @Output() closeButtonEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() backButtonEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() termsSubmittedEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() title = 'LEGAL_DOCUMENTS_TITLE';
  @Input() subtitle = 'LEGAL_DOCUMENTS_SUBTITLE';
  @Input() ctaKey = 'ACCEPT';
  @Input() legalType: 'consent' | 'acknowledgement' = 'consent';

  sub = new Subscription();
  readonly legalDocumentsService = inject(LegalDocumentsService);
  processing: Observable<boolean> = this.legalDocumentsService.processing;
  requiredDocs$: Observable<LegalDocsResponsePayloadItems[]> = this.legalDocumentsService.requiredDocuments;
  docsSubmitted: Observable<boolean> = this.legalDocumentsService.docsSubmitted;
  deferrable: Observable<boolean> = this.legalDocumentsService.deferrable;
  displayDocsRequiredModal: Observable<boolean> = this.legalDocumentsService.displayDocsRequiredModal;
  displayGeneralError: Observable<boolean> = this.legalDocumentsService.displayGeneralError;
  docCopyKeys$: Observable<LegalDocsCopyKeys> = this.legalDocumentsService.docCopyKeys;

  readonly docCopyKeys = toSignal(this.docCopyKeys$, { initialValue: null });

  readonly titleSignal = signal(this.title);
  readonly subtitleSignal = signal(this.subtitle);
  readonly titleCopy = computed(() => {
    const doc = this.docCopyKeys();
    return doc?.titleCopy?.toUpperCase() || this.titleSignal();
  });

  readonly descriptionCopy = computed(() => {
    const doc = this.docCopyKeys();
    return doc?.descriptionCopy?.toUpperCase() || this.subtitleSignal();
  });
  confirmDataForm: FormGroup = new FormGroup({
    confirmCheckbox: new FormControl('', [Validators.requiredTrue]),
  });
  showSpinner = false;
  config: LegalDocsPageConfigInterface;
  private pageConfig: KnownPageConfigInterface | undefined;

  constructor(private activatedRoute: ActivatedRoute) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['title']) {
      this.titleSignal.set(this.title);
    }
    if (changes['subtitle']) {
      this.subtitleSignal.set(this.subtitle);
    }
  }
  ngOnInit(): void {
    this.pageConfig = this.activatedRoute?.snapshot?.data?.pageConfig;

    if (this.documentsKey) {
      setTimeout(() => {
        this.legalDocumentsService.getRequiredDocuments(
          this.documentsKey as DocumentsKeyEnum,
          this.pageConfig?.bypassOnServerError || false
        );
      }, 0);
    } else if (this.pageConfig?.documentsKey) {
      this.config = this.activatedRoute?.snapshot?.data?.pageConfig;
      this.documentsKey = this.config.documentsKey;
      this.pageOnForwardCtaPath = this.config.pageOnForwardCtaPath ?? '';
      this.closeButtonRoute = this.config.closeButtonRoute ?? undefined;
      this.showCloseButton = this.config.showCloseButton ?? false;
      this.showBackbutton = this.config.showBackbutton ?? true;
      setTimeout(() => {
        this.legalDocumentsService.getRequiredDocuments(
          this.documentsKey as DocumentsKeyEnum,
          this.pageConfig?.bypassOnServerError || false
        );
      }, 0);
    } else {
      console.warn('Legal Docs component needs configs to run');
    }

    this.sub.add(
      this.requiredDocs$.subscribe((docs) => {
        this.showSpinner = false;
        this.handleConfirmDataForm();
      })
    );

    this.sub.add(
      this.docsSubmitted.subscribe((resp) => {
        this.docsSubmittedResponse(resp);
      })
    );
  }

  onSubmit() {
    this.legalDocumentsService.acceptRequiredDocuments();
  }

  handleCloseClick() {
    if (this.closeButtonRoute) {
      MessageBusEventUtil.routeToPath(this.closeButtonRoute.join('/'));
    } else {
      this.closeButtonEvent.emit(true);
    }
  }

  docsSubmittedResponse(success: boolean) {
    if (success) {
      if (!this.pageOnForwardCtaPath) {
        this.termsSubmittedEvent.emit(true);
      } else {
        MessageBusEventUtil.routeToPath(this.pageOnForwardCtaPath.join('/'));
      }
    } else {
      this.termsSubmittedEvent.emit(false);
      //unknown error?
    }
  }

  refetchDocuments() {
    this.handleConfirmDataForm();
    this.legalDocumentsService.getRequiredDocuments(
      this.documentsKey as DocumentsKeyEnum,
      this.pageConfig?.bypassOnServerError || false
    );
  }

  closeError() {
    this.legalDocumentsService.hideGeneralError();
  }

  handleDocClick(downloadUrl: string, $event: MouseEvent) {
    $event.stopPropagation();
    $event.preventDefault();
    MessageBusEventUtil.sendOutgoing({
      event: MessageBusOutgoingEventKey.EXTERNAL_LINK,
      bridgeSetting: {
        data: {
          url: downloadUrl,
          chatIcon: false,
        },
      },
    });
  }

  emitBackButtonEvent() {
    if (!this.skipActionEmit) this.backButtonEvent.emit(true);
  }

  deferAcceptance() {
    this.showSpinner = true;
    this.legalDocumentsService.acceptRequiredDocuments(LegalDocsAcceptActions.DEFERRED);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  handleConfirmDataForm() {
    if (this.legalType === 'acknowledgement') {
      this.confirmDataForm.controls.confirmCheckbox.setValue(true);
    } else {
      this.confirmDataForm.reset();
    }
  }
}
